<script setup lang="ts">
import { env } from '@/config/env';
import { useAuthStore } from '@/stores/auth';
import { useSubscriptionStore } from '@/stores/subscription';
import type { QueryParams } from '@/types';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const props = defineProps<{
  queryParams?: QueryParams;
}>();

const router = useRouter();
const route = useRoute();

const authStore = useAuthStore();
const { cancelUrl } = storeToRefs(authStore);

const subscriptionStore = useSubscriptionStore();
const { isLoading, needsPaymentMethod } = storeToRefs(subscriptionStore);

const cancelUrlFallback = computed(() => {
  return cancelUrl.value ?? env.CANCEL_URL;
});

const goBack = () => {
  console.warn('🔙 goBack called, redirecting to cancel URL:', cancelUrl.value);

  if (needsPaymentMethod.value) {
    window.location.href = cancelUrlFallback.value;
  } else if (window.history.length > 1) {
    router.back();
  } else {
    router.push('/');
  }
};

const isBackButtonVisible = computed(() => {
  return ['home', 'add-payment-method'].includes(route.name as string);
});

const headerClasses = computed(() => {
  return isBackButtonVisible.value ? 'flex items-center justify-between' : 'flex items-center justify-center';
});

const logoClasses = computed(() => {
  return isBackButtonVisible.value 
    ? 'absolute left-1/2 transform -translate-x-1/2' 
    : 'relative';
});
</script>

<template>
  <header :class="headerClasses">
    <template v-if='isBackButtonVisible'>
      <a v-if="route.name === `home`" :href="cancelUrl" class="p-2">
        <img src="/arrow-left.svg" alt="Back" class="size-5" />
      </a>

      <button v-else @click="goBack" class="p-2">
        <img src="/arrow-left.svg" alt="Back" class="size-5" />
      </button>
    </template>

    <div :class="logoClasses">
      <img src="/oneleaf-logo.svg" alt="OneLeaf" class="h-7" />
    </div>

    <div v-if="isBackButtonVisible" class="w-10 flex justify-end">
      <svg v-if="isLoading" class="size-6 animate-spin text-primary" xmlns="http://www.w3.org/2000/svg" fill="none"
        viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
        </path>
      </svg>
    </div>
  </header>
</template>